// IndexedDB 图片存储工具（保存时尽量上传拿 serverUrl，展示统一用公网 /uploads/ 或 OSS 地址，不用 blob）
// 按登录用户隔离库名：funding_image_storage__u__{userId}，避免换号 id 碰撞读到旧图
import { imageStorageAPI } from './api';
import { pickUrlFromImageStorageResponse, normalizePersistedImageUrl } from './imageUrlUtils';
import { getIpeUserLocalOwnerId } from './ipeUserLocalState';
import { ownerScopedIndexedDbName } from './ipeUserIndexedDb';

/** 与 imageUrlUtils 一致：从响应中解析 /uploads/ 或 OSS https URL */
const pickUploadUrlFromSaveResponse = (res) => {
  const url = pickUrlFromImageStorageResponse(res);
  return url || null;
};

const DB_NAME = 'funding_image_storage';
const DB_VERSION = 1;
const STORE_NAME = 'images';

let dbInstance = null;
/** @type {string|null} */
let dbInstanceOwner = null;
/** @type {string|null} */
let dbInstanceName = null;

function closeDbInstance() {
  if (dbInstance) {
    try {
      dbInstance.close();
    } catch {
      /* ignore */
    }
  }
  dbInstance = null;
  dbInstanceOwner = null;
  dbInstanceName = null;
}

/** 换号 / 退出时关闭连接，避免跨用户复用同一 IDB 连接 */
export function resetImageStorageForOwnerChange() {
  closeDbInstance();
}

// 打开当前用户作用域数据库
const openDB = () => {
  return new Promise((resolve, reject) => {
    const owner = getIpeUserLocalOwnerId();
    const name = ownerScopedIndexedDbName(DB_NAME);
    if (!owner || !name) {
      reject(new Error('未绑定登录用户，无法访问本地图片存储'));
      return;
    }

    if (dbInstance && dbInstanceOwner === owner && dbInstanceName === name) {
      resolve(dbInstance);
      return;
    }

    closeDbInstance();

    const request = indexedDB.open(name, DB_VERSION);

    request.onerror = () => {
      reject(new Error('打开数据库失败'));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      dbInstanceOwner = owner;
      dbInstanceName = name;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
};

// 将 base64 数据 URL 转换为 Blob
const dataURLToBlob = (dataURL) => {
  if (!dataURL || typeof dataURL !== 'string') {
    throw new Error('无效的 data URL');
  }
  
  // 如果是 Object URL (blob:)，直接返回 null，需要特殊处理
  if (dataURL.startsWith('blob:')) {
    return null;
  }
  
  // 检查是否是有效的 data URL
  if (!dataURL.startsWith('data:')) {
    throw new Error('不是有效的 data URL 格式');
  }
  
  const arr = dataURL.split(',');
  if (arr.length < 2) {
    throw new Error('data URL 格式不正确');
  }
  
  const mimeMatch = arr[0].match(/:(.*?);/);
  if (!mimeMatch || !mimeMatch[1]) {
    throw new Error('无法从 data URL 中提取 MIME 类型');
  }
  
  const mime = mimeMatch[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

// 将 Blob 转换为 Object URL
const blobToObjectURL = (blob) => {
  return URL.createObjectURL(blob);
};

// 保存图片到 IndexedDB
export const saveImageToIndexedDB = async (imageId, imageUrl, options = {}) => {
  const { skipServerUpload = false } = options;
  try {
    if (!imageId || imageUrl == null || imageUrl === '') {
      throw new Error('图片 ID 或数据不能为空');
    }
    
    let blob = null;
    let dataURL = null;

    // 直接传入 Blob/File（图片空间流水线已压缩文件）
    if (typeof Blob !== 'undefined' && imageUrl instanceof Blob) {
      blob = imageUrl;
    } else if (typeof imageUrl === 'string' && imageUrl.startsWith('blob:')) {
      try {
        const response = await fetch(imageUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        blob = await response.blob();
        // 将 Blob 转换为 data URL 以便后续使用
        dataURL = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('FileReader 读取失败'));
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        // 如果 blob URL 失效，尝试从 IndexedDB 中查找是否已有该图片
        console.warn(`获取 Object URL 的 Blob 失败，尝试从 IndexedDB 查找:`, error);
        try {
          const existing = await getImageFromIndexedDB(imageId);
          if (existing) {
            // 如果 IndexedDB 中已有该图片，直接返回成功

            return true;
          }
        } catch (dbError) {
          console.warn(`从 IndexedDB 查找图片 ${imageId} 失败:`, dbError);
        }
        // 如果无法从 blob URL 获取且 IndexedDB 中不存在该图片，抛出错误
        throw new Error('无法从 Object URL 获取图片数据，且 IndexedDB 中不存在该图片');
      }
    } else if (typeof imageUrl === 'string' && imageUrl.startsWith('data:')) {
      // 如果是 data URL，转换为 Blob
      blob = dataURLToBlob(imageUrl);
      dataURL = imageUrl;
    } else if (typeof imageUrl === 'string' && normalizePersistedImageUrl(imageUrl)) {
      const serverUrl = normalizePersistedImageUrl(imageUrl);
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put({ id: imageId, serverUrl, savedAt: Date.now() });
        request.onsuccess = () => resolve(true);
        request.onerror = (e) => reject(e.target.error);
      });
    } else {
      throw new Error('不支持的图片 URL 格式，仅支持 data URL、Object URL 或 /uploads/ 服务器路径');
    }
    
    if (!blob) {
      throw new Error('无法创建 Blob 对象');
    }
    
    // 上传到服务器拿到 serverUrl，展示时用公网地址不用 blob
    let serverUrl = null;
    if (!skipServerUpload) {
      try {
        const res = await imageStorageAPI.save(blob || dataURL, { folder: 'image-space', registerImageSpace: false });
        const u = pickUploadUrlFromSaveResponse(res);
        if (u) serverUrl = u;
      } catch (e) {
        console.warn('保存时上传服务器失败，仅存本地:', e);
      }
    }
    
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      // B2：IndexedDB 只持久化 Blob（+可选 serverUrl），禁止再写原图 dataURL
      // T-A19：savedAt / pendingUpload / fileName 供定时 GC 与刷新恢复
      const record = {
        id: imageId,
        blob,
        serverUrl: serverUrl || undefined,
        savedAt: Date.now(),
      };
      if (skipServerUpload && String(imageId).startsWith('LIB-')) {
        record.pendingUpload = true;
      }
      if (typeof File !== 'undefined' && imageUrl instanceof File && imageUrl.name) {
        record.fileName = imageUrl.name;
      }
      const request = store.put(record);
      
      request.onsuccess = () => {
        resolve(true);
      };
      
      request.onerror = (e) => {
        const error = e.target.error;
        // 检查是否是存储空间不足错误
        if (error && (error.name === 'QuotaExceededError' || error.code === 22)) {
          console.warn(`IndexedDB 存储空间不足，尝试清理旧数据:`, error);
          reject(new Error('QUOTA_EXCEEDED'));
        } else {
          console.error(`保存图片 ${imageId} 到 IndexedDB 失败:`, e);
          reject(new Error('保存图片到 IndexedDB 失败'));
        }
      };
    });
  } catch (error) {
    console.error(`保存图片 ${imageId} 失败:`, error);
    throw error;
  }
};

// 从 IndexedDB 读取图片
export const getImageFromIndexedDB = async (imageId) => {
  try {
    if (!imageId) {
      console.warn('图片 ID 为空');
      return null;
    }
    if (!getIpeUserLocalOwnerId()) {
      return null;
    }
    
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(imageId);
      
      request.onsuccess = () => {
        const result = request.result;
        // 优先返回服务器路径；其次 Blob Object URL；兼容旧记录 dataURL
        if (result && result.serverUrl) {
          resolve(result.serverUrl);
          return;
        }
        if (result && result.blob) {
          resolve(blobToObjectURL(result.blob));
        } else if (result && result.dataURL) {
          resolve(result.dataURL);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = (e) => {
        console.error(`从 IndexedDB 读取图片 ${imageId} 失败:`, e);
        reject(new Error('从 IndexedDB 读取图片失败'));
      };
    });
  } catch (error) {
    console.error(`读取图片 ${imageId} 失败:`, error);
    return null;
  }
};

// 批量保存图片到 IndexedDB
export const saveImagesToIndexedDB = async (images) => {
  const results = await Promise.allSettled(
    images.map((img) => {
      return saveImageToIndexedDB(img.id, img.src);
    })
  );
  
  const failed = results.filter(r => r.status === 'rejected');
  if (failed.length > 0) {
    console.warn('部分图片保存失败:', failed);
  }
  
  return results.every(r => r.status === 'fulfilled');
};

// 从 IndexedDB 删除图片
export const deleteImageFromIndexedDB = async (imageId) => {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(imageId);
      
      request.onsuccess = () => {
        resolve(true);
      };
      
      request.onerror = () => {
        reject(new Error('从 IndexedDB 删除图片失败'));
      };
    });
  } catch (error) {
    console.error('删除图片失败:', error);
    throw error;
  }
};

// 批量删除图片
export const deleteImagesFromIndexedDB = async (imageIds) => {
  const results = await Promise.allSettled(
    imageIds.map(id => deleteImageFromIndexedDB(id))
  );
  
  const failed = results.filter(r => r.status === 'rejected');
  if (failed.length > 0) {
    console.warn('部分图片删除失败:', failed);
  }
  
  return results.every(r => r.status === 'fulfilled');
};

// 获取所有图片ID列表
export const getAllImageIds = async () => {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAllKeys();
      
      request.onsuccess = () => {
        resolve(request.result || []);
      };
      
      request.onerror = () => {
        reject(new Error('获取图片列表失败'));
      };
    });
  } catch (error) {
    console.error('获取图片列表失败:', error);
    throw error;
  }
};

/**
 * T-A19：读取原始 IDB 记录（含 blob / savedAt / pendingUpload），不创建 Object URL。
 * @returns {Promise<object|null>}
 */
export const getImageRecordFromIndexedDB = async (imageId) => {
  try {
    if (!imageId || !getIpeUserLocalOwnerId()) return null;
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(imageId);
      request.onsuccess = () => {
        resolve(request.result || null);
      };
      request.onerror = (e) => {
        console.error(`从 IndexedDB 读取记录 ${imageId} 失败:`, e);
        reject(new Error('从 IndexedDB 读取记录失败'));
      };
    });
  } catch (error) {
    console.error(`读取图片记录 ${imageId} 失败:`, error);
    return null;
  }
};

// 清理不在使用中的图片（保留指定的图片ID列表）
export const cleanupUnusedImages = async (keepImageIds = []) => {
  try {
    const allImageIds = await getAllImageIds();
    const keepSet = new Set(keepImageIds);
    const imagesToDelete = allImageIds.filter(id => !keepSet.has(id));
    
    if (imagesToDelete.length === 0) {
      return { deleted: 0 };
    }
    
    // 删除不在保留列表中的图片（最多删除一半，避免一次性删除太多）
    const deleteCount = Math.ceil(imagesToDelete.length / 2);
    const toDelete = imagesToDelete.slice(0, deleteCount);
    
    await deleteImagesFromIndexedDB(toDelete);
    
    return { deleted: toDelete.length };
  } catch (error) {
    console.error('清理未使用的图片失败:', error);
    throw error;
  }
};

// 清理所有图片（用于清理旧数据）
export const clearAllImages = async () => {
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();
      
      request.onsuccess = () => {
        resolve(true);
      };
      
      request.onerror = () => {
        reject(new Error('清理图片失败'));
      };
    });
  } catch (error) {
    console.error('清理图片失败:', error);
    throw error;
  }
};

// ==================== 持久化存储支持 ====================

// 请求持久化存储权限（可以获得更大的存储配额）
export const requestPersistentStorage = async () => {
  try {
    if ('storage' in navigator && 'persist' in navigator.storage) {
      const isPersisted = await navigator.storage.persist();
      return {
        success: isPersisted,
        message: isPersisted 
          ? '已获得持久化存储权限，可以使用更大的存储空间' 
          : '持久化存储权限被拒绝，将使用临时存储',
      };
    }
    return {
      success: false,
      message: '浏览器不支持持久化存储 API',
    };
  } catch (error) {
    console.error('请求持久化存储失败:', error);
    return {
      success: false,
      message: `请求持久化存储失败: ${error.message}`,
    };
  }
};

// 检查是否已获得持久化存储权限
export const checkPersistentStorage = async () => {
  try {
    if ('storage' in navigator && 'persisted' in navigator.storage) {
      const isPersisted = await navigator.storage.persisted();
      return isPersisted;
    }
    return false;
  } catch (error) {
    console.error('检查持久化存储状态失败:', error);
    return false;
  }
};

// 获取存储配额信息
export const getStorageQuota = async () => {
  try {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const isPersisted = await checkPersistentStorage();
      
      return {
        quota: estimate.quota || 0, // 总配额（字节）
        usage: estimate.usage || 0, // 已使用（字节）
        usageDetails: estimate.usageDetails || {}, // 详细使用情况
        available: (estimate.quota || 0) - (estimate.usage || 0), // 可用空间（字节）
        isPersistent: isPersisted, // 是否为持久化存储
      };
    }
    return null;
  } catch (error) {
    console.error('获取存储配额失败:', error);
    return null;
  }
};

// 格式化存储大小
export const formatStorageSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

// 初始化持久化存储（在应用启动时调用）
export const initializePersistentStorage = async () => {
  try {
    // 检查是否已获得持久化存储权限
    const isPersisted = await checkPersistentStorage();
    
    if (!isPersisted) {
      // 尝试请求持久化存储权限
      const result = await requestPersistentStorage();
      if (result.success) {

        return {
          success: true,
          message: result.message,
        };
      } else {
        console.warn('⚠️ 持久化存储未启用:', result.message);
        return {
          success: false,
          message: result.message,
        };
      }
    } else {
     
      return {
        success: true,
        message: '持久化存储已启用',
      };
    }
  } catch (error) {
    console.error('初始化持久化存储失败:', error);
    return {
      success: false,
      message: `初始化失败: ${error.message}`,
    };
  }
};
